/**
 * acdc-cart-drawer-smooth.js
 *
 * Intercepts cart events to smooth the cart drawer update cycle.
 * - Adds .is-updating BEFORE theme.js processes (capture phase)
 * - Clears updating state after DOM replacement completes
 * - Re-initializes GWP visibility and accordion after replaceChildren()
 */
(function () {
  'use strict';

  const UPDATING_CLASS = 'is-updating';
  const COMPLETE_CLASS = 'is-update-complete';
  let completeTimer = null;
  let mutationTimer = null;

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  // --- Updating state management ---

  function markUpdating() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove(COMPLETE_CLASS);
    drawer.classList.add(UPDATING_CLASS);

    // Immediately hide footer and recs before DOM replacement so
    // they don't flash when new DOM is painted without .footer-ready
    var footer = document.querySelector('.cart-drawer__footer-inner');
    if (footer) footer.classList.remove(FOOTER_READY_CLASS);
    var recs = document.querySelector('cart-drawer product-recommendations');
    if (recs) recs.classList.remove(FOOTER_READY_CLASS);
  }

  function markComplete() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove(UPDATING_CLASS);
    drawer.classList.add(COMPLETE_CLASS);

    // Remove the complete class after transition finishes
    clearTimeout(completeTimer);
    completeTimer = setTimeout(function () {
      drawer.classList.remove(COMPLETE_CLASS);
    }, 200);

    // Re-init GWP after DOM replacement
    reinitGwpVisibility();
    reinitGwpAccordion();
    reinitGwpCarouselArrows();
  }

  // --- Capture-phase listeners (run BEFORE theme.js bubble listeners) ---

  document.addEventListener('cart:change', markUpdating, true);
  document.addEventListener('cart:refresh', markUpdating, true);

  // Listen for theme.js completion event (dispatched after second replaceChildren)
  document.addEventListener('cart-drawer:refreshed', function () {
    markComplete();
  });

  // --- MutationObserver fallback ---
  // In case cart-drawer:refreshed doesn't fire, watch for childList changes

  function setupMutationObserver() {
    const drawer = getDrawer();
    if (!drawer) return;

    var observer = new MutationObserver(function () {
      // Debounce — wait for the second replaceChildren() to finish
      clearTimeout(mutationTimer);
      mutationTimer = setTimeout(function () {
        if (drawer.classList.contains(UPDATING_CLASS)) {
          markComplete();
        }
      }, 200);
    });

    observer.observe(drawer, { childList: true, subtree: false });
  }

  // --- GWP visibility (replaces display:none/block toggle) ---

  function reinitGwpVisibility() {
    // Multiple instances can coexist (cart drawer + cart page); update each.
    var gwpSections = document.querySelectorAll('.minicart-gwp-wrap');
    if (!gwpSections.length) return;

    var threshold = window.ACDC && window.ACDC.Data && window.ACDC.Data.gwpSectionThreshold;
    if (!threshold || threshold === '') return;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var isVisible = Number(cart.total_price) >= Number(threshold);
        gwpSections.forEach(function (gwpSection) {
          if (isVisible) {
            gwpSection.classList.add('gwp-visible');
          } else {
            gwpSection.classList.remove('gwp-visible');
          }
          gwpSection.style.removeProperty('display');
        });

        if (typeof window.syncGwpButtonStates === 'function') {
          window.syncGwpButtonStates(cart.items);
        }
      })
      .catch(function () {});
  }

  // --- GWP accordion re-init ---
  // The inline script in minicart-gwp.liquid only runs on DOMContentLoaded,
  // so after replaceChildren() the click handler is lost. Re-attach it here.

  function reinitGwpAccordion() {
    // Multiple instances can coexist (cart drawer + cart page); bind each one.
    var gwpSections = document.querySelectorAll('.minicart-gwp-wrap');
    gwpSections.forEach(function (gwpSection) {
      var heading = gwpSection.querySelector('.minicart-gwp-heading');
      if (!heading) return;

      // Avoid duplicate listeners by using a data attribute flag
      if (heading.dataset.accordionBound === 'true') return;
      heading.dataset.accordionBound = 'true';

      heading.addEventListener('click', function () {
        gwpSection.classList.toggle('active');
      });
    });
  }

  // --- GWP carousel arrows re-init ---
  // Re-attach scroll listener for prev/next button state after DOM replacement

  function reinitGwpCarouselArrows() {
    // Multiple instances can coexist (cart drawer + cart page); bind each one.
    var gwpSections = document.querySelectorAll('.minicart-gwp-wrap');
    gwpSections.forEach(function (gwpSection) {
      var carousel = gwpSection.querySelector('scroll-carousel');
      var prevBtn = gwpSection.querySelector('carousel-prev-button button');
      var nextBtn = gwpSection.querySelector('carousel-next-button button');
      if (!carousel || !prevBtn || !nextBtn) return;

      // Avoid duplicates
      if (carousel.dataset.arrowsBound === 'true') return;
      carousel.dataset.arrowsBound = 'true';

      carousel.addEventListener('scroll', function () {
        var scrollLeft = Math.round(Math.abs(carousel.scrollLeft));
        var maxScroll = carousel.scrollWidth - carousel.clientWidth;
        prevBtn.disabled = scrollLeft <= 0;
        nextBtn.disabled = scrollLeft >= maxScroll - 1;
      });
    });
  }

  // --- Footer stabilization ---
  // Third-party apps (Wrap-in, package protection) inject content into the
  // footer async after render. Hide footer initially, reveal once apps inject
  // or after a timeout fallback.

  var footerObserver = null;
  var footerTimeout = null;
  var FOOTER_READY_CLASS = 'footer-ready';
  var FOOTER_WAIT_MS = 800; // max wait before revealing anyway

  function revealFooter() {
    clearTimeout(footerTimeout);
    if (footerObserver) {
      footerObserver.disconnect();
      footerObserver = null;
    }
    var footer = document.querySelector('.cart-drawer__footer-inner');
    if (footer) {
      footer.classList.add(FOOTER_READY_CLASS);
    }
    // Reveal product recommendations together with the footer
    var recs = document.querySelector('cart-drawer product-recommendations');
    if (recs) {
      recs.classList.add(FOOTER_READY_CLASS);
    }
  }

  function setupFooterObserver() {
    var footer = document.querySelector('.cart-drawer__footer-inner');
    if (!footer) return;

    // Reset ready classes so content starts hidden for the new cycle
    footer.classList.remove(FOOTER_READY_CLASS);
    var recs = document.querySelector('cart-drawer product-recommendations');
    if (recs) {
      recs.classList.remove(FOOTER_READY_CLASS);
    }

    // Disconnect any previous observer
    if (footerObserver) {
      footerObserver.disconnect();
    }

    var ptwMount = footer.querySelector('#ptw-wrapin');
    var buttonGroup = footer.querySelector('.button-group');

    // Track which injections we're waiting for
    var ptwReady = !ptwMount; // if no mount point, nothing to wait for
    var buttonReady = false;

    footerObserver = new MutationObserver(function () {
      // Check if Wrap-in has injected content
      if (ptwMount && ptwMount.children.length > 0) {
        ptwReady = true;
      }
      // Check if package protection has modified the button area
      // (it typically adds elements outside/around the original button group)
      if (buttonGroup) {
        var parent = buttonGroup.parentElement;
        if (parent && parent.children.length > 1) {
          buttonReady = true;
        }
      }

      if (ptwReady) {
        // Delay to let package protection finish injecting too
        clearTimeout(footerTimeout);
        footerTimeout = setTimeout(revealFooter, 400);
      }
    });

    // Observe the entire footer subtree for injections
    footerObserver.observe(footer, { childList: true, subtree: true });

    // Timeout fallback — reveal even if apps don't inject (e.g. app disabled)
    clearTimeout(footerTimeout);
    footerTimeout = setTimeout(revealFooter, FOOTER_WAIT_MS);
  }

  // Re-setup observers after each cart update completes
  var origMarkComplete = markComplete;
  markComplete = function () {
    origMarkComplete();
    setupFooterObserver();
  };

  // --- Drawer open stabilization ---
  // When the cart drawer opens (e.g. clicking cart icon), run the same
  // footer/recs stabilization so async content fades in smoothly.

  function setupDrawerOpenListener() {
    var drawer = getDrawer();
    if (!drawer) return;

    drawer.addEventListener('dialog:before-show', function () {
      setupFooterObserver();
    });
  }

  // --- Init on DOM ready ---

  function initAll() {
    setupMutationObserver();
    reinitGwpAccordion();
    reinitGwpCarouselArrows();
    setupFooterObserver();
    setupDrawerOpenListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
